import TypographyTooltipEllipsis from "@entrilia/shared/components/TypographyTooltipEllipsis.tsx";
import React from "react";
import { useSelector } from "react-redux";
import { BiAggregation } from "../../../../../../../../api/types/fieldConfigurationTypes.ts";
import { ItemDataType } from "../../../../../../../../api/types/svTypes.ts";
import { useAppDispatch } from "../../../../store/hooks/useAppDispatch";
import { selectAggregationFields } from "../../../../store/selectors/fieldsStateSelectors";
import { fieldsStateActions } from "../../../../store/thunks/fieldStatesThunks";
import { isAggregation, isCustomColumn, isMeasure } from "../../../../utilities/fieldsHelper.ts";
import SwitchField from "../../../common/SwitchField";
import { getDisplayCaption } from "../../../viewer/common/fields/field-captions/captionsHelper.ts";
import { supportsSubtotals } from "../../common/fields/custom-field/utilities/calculationTypeCapabilities.ts";
import { isNumericField } from "../../utils/isNumericField.ts";
import ExpandeGroup from "./ExpandeGroup.tsx";
import StyleItem from "./StyleItem.tsx";

interface Props {
  title: string;
  readonlyMode: boolean;
  includeNumericDimensions?: boolean;
  switchLabel?: string;
}

export default function MeasureSettings({ title, readonlyMode, includeNumericDimensions, switchLabel }: Props) {
  const dispatch = useAppDispatch();
  const fieldsAreaValues = useSelector(selectAggregationFields);

  const numericMeasures = React.useMemo(() => {
    return fieldsAreaValues.filter((f) => {
      if (includeNumericDimensions) {
        return (
          isNumericField(f) &&
          f.config.aggregation !== BiAggregation.StringAgg &&
          (!isCustomColumn(f) || supportsSubtotals(f.config.calculationType))
        );
      }
      if (isMeasure(f)) {
        return f.meta.type === ItemDataType.Numeric && !f.meta.capabilities.isScalar;
      } else if (isAggregation(f) || (isCustomColumn(f) && supportsSubtotals(f.config.calculationType))) {
        return isNumericField(f);
      }
      return false;
    });
  }, [fieldsAreaValues, includeNumericDimensions]);

  if (numericMeasures.length === 0) {
    return <></>;
  }

  return (
    <ExpandeGroup title={title}>
      {numericMeasures.map((field, index) => {
        return (
          <StyleItem
            key={field.config.guid}
            showBorder={numericMeasures.length > 1 && index < numericMeasures.length - 1}
          >
            <TypographyTooltipEllipsis
              typographyProps={{
                variant: "subtitle2",
                sx: { pb: 1, width: "100%", fontWeight: 500 },
              }}
              text={getDisplayCaption(field.meta.caption, field.config.customLabel, field.config.systemLabel)}
            />
            <SwitchField
              label={switchLabel ?? "Calculate Subtotals"}
              checked={!field.config.hideAggregation}
              onChange={(hideAggregation) =>
                dispatch(
                  fieldsStateActions.updateFieldConfig({
                    field,
                    changes: { hideAggregation: !hideAggregation },
                  })
                )
              }
              typographyProps={{ sx: { color: "text.secondary" } }}
              disabled={readonlyMode}
            />
          </StyleItem>
        );
      })}
    </ExpandeGroup>
  );
}
